import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useLocation } from 'react-router-dom';
import "./ProductDivider.css";

const ProductDivider = ({ flip = false }) => {
  const location = useLocation();
  const isIndustrial = location.pathname.includes('/industrial');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef(null);
  
  const industrialImages = [
    `${process.env.PUBLIC_URL}/images/B2BRRBottlesHeroImage.png`,
    `${process.env.PUBLIC_URL}/images/B2BRRBottlesHeroImage2.png`,
    `${process.env.PUBLIC_URL}/images/B2BRRBottlesHeroImage3.png`,
    `${process.env.PUBLIC_URL}/images/B2BRRBottlesHeroImage4.png`
  ];

  const imageSrc = isIndustrial
    ? industrialImages[currentImageIndex]
    : `${process.env.PUBLIC_URL}/images/RRBottlesHeroImage.png`;

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % industrialImages.length);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? industrialImages.length - 1 : prevIndex - 1
    );
  };

  // Clear any existing interval when component unmounts or dependencies change
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, [isIndustrial, isPaused]);

  // Set up the interval
  useEffect(() => {
    if (!isIndustrial || isPaused) return;

    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set new interval
    intervalRef.current = setInterval(() => {
      nextSlide();
    }, 4000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isIndustrial, isPaused]);

  // Reset transition flag after animation completes
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 800); // Match this with the transition duration in CSS

    return () => clearTimeout(timer);
  }, [currentImageIndex]);

  return (
    <div 
      className={`product-divider ${isIndustrial ? 'industrial' : ''} ${flip ? 'flip' : ''}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="carousel-container">
        <div 
          className="carousel-track"
          style={{
            transform: `translateX(-${currentImageIndex * 100}%)`,
            transition: isTransitioning ? 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
          }}
        >
          {isIndustrial ? (
            industrialImages.map((image, index) => (
              <div 
                key={index} 
                className={`carousel-slide ${currentImageIndex === index ? 'active' : ''}`}
              >
                <img
                  src={image}
                  alt={`Product ${index + 1}`}
                  className="product-divider-image"
                />
              </div>
            ))
          ) : (
            <div className="carousel-slide active">
              <img
                src={imageSrc}
                alt="Product"
                className="product-divider-image"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ProductDivider.propTypes = {
  flip: PropTypes.bool,
};

ProductDivider.defaultProps = {
  flip: false,
};

export default ProductDivider;